import { useState, type ReactNode } from 'react';
import { Radio } from '@vd/ui';
import { RecordingModeArt } from './CaptureIllustrations';
import styles from '../../routes/settings/Settings.module.css';

export type ProjectRecordingMode = 'video' | 'audio' | null;

/**
 * What a walkthrough of this project records (TOADO-383).
 *
 * Inherit is the default and stays a real choice rather than a preselected
 * mode: a project that documents a visual product wants the screen, one
 * whose tickets are mostly copy and spacing does not, and somebody who works
 * on both should not have to remember which they last chose. Setting this
 * answers for everybody who records the project, because it is a property of
 * the work and not of the person doing it.
 *
 * Saved on the click rather than behind the General tab's Save button,
 * because it is a discrete choice and not a field being drafted - the same
 * reason the bug-intake toggle saves immediately.
 */
export function RecordingModeSection({
  mode, onChange, inheritFooter, heading = 'Walkthrough recording',
}: {
  mode: ProjectRecordingMode;
  /** Overridden where the surface around it has already said this. */
  heading?: string;
  /**
   * Shown under the inherit choice, outside its label.
   *
   * Outside on purpose: the label is the radio's click target, so a button
   * inside it would select the choice as a side effect of being pressed.
   */
  inheritFooter?: ReactNode;
  /**
   * Saves the mode and makes it the value of `mode`.
   *
   * Owns reporting its own failure - this control only gives the radio back.
   * It may reject; nothing here depends on which way it settles.
   */
  onChange: (mode: ProjectRecordingMode) => Promise<void>;
}) {
  /**
   * The clicked choice, shown until the save settles.
   *
   * Dropped on the way out rather than only on a throw. The save reports its
   * own failure and resolves either way, so a rollback that waited for a
   * rejection never ran: a refused save left the choice somebody did not make
   * looking selected, and clicking it again did nothing because it already
   * was. Clearing it unconditionally means the radio always ends up showing
   * what the server holds - the new mode after a save that worked, the old
   * one after a save that did not.
   *
   * There is no flash in the working case: the save writes the project the
   * server returned straight into the query, so `mode` has already changed
   * by the time this clears. It does not wait on a refetch, which is a
   * second thing that can fail (review round 2).
   */
  const [pending, setPending] = useState<ProjectRecordingMode | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const shown = pending === undefined ? mode : pending;

  const choose = async (next: ProjectRecordingMode) => {
    if (saving || next === shown) return;
    setPending(next);
    setSaving(true);
    try {
      await onChange(next);
    } catch {
      // Swallowed here because the save has already told somebody, and
      // because `choose` is called from a click handler that cannot await it:
      // a rejection escaping this would be an unhandled one. The rollback is
      // in the `finally` either way, so both outcomes land in the same place
      // (review round 2).
    } finally {
      setSaving(false);
      setPending(undefined);
    }
  };

  return (
    <section className={styles.section}>
      <h3 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600 }}>{heading}</h3>
      <p className={styles.sub} style={{ marginTop: 0 }}>
        What a recording of this project captures. This applies to everyone who records it.
      </p>
      <div
        role="radiogroup"
        aria-label="Walkthrough recording for this project"
        style={{ display: 'grid', gap: 8, opacity: saving ? 0.7 : 1 }}
      >
        <RecordingModeOption
          selected={shown === null}
          disabled={saving}
          title="Use each person's own setting"
          desc="Whatever the person recording has chosen in their Toado extension options."
          art={<RecordingModeArt mode="own" />}
          onSelect={() => void choose(null)}
          footer={inheritFooter}
        />
        <RecordingModeOption
          selected={shown === 'video'}
          disabled={saving}
          title="Screen and narration"
          desc="Records the shared window or tab with your voice. Best for anything visual."
          art={<RecordingModeArt mode="video" />}
          onSelect={() => void choose('video')}
        />
        <RecordingModeOption
          selected={shown === 'audio'}
          disabled={saving}
          title="Narration only"
          desc="Records your voice and nothing else. Pins still capture screenshots, and a long walkthrough uploads in a fraction of the time."
          art={<RecordingModeArt mode="audio" />}
          onSelect={() => void choose('audio')}
        />
      </div>
    </section>
  );
}

function RecordingModeOption({ selected, disabled, title, desc, onSelect, footer, art }: {
  selected: boolean; disabled: boolean; title: string; desc: string;
  onSelect: () => void; footer?: ReactNode;
  /** A drawing of what it records, on the right (Jason, 10 Oct). Decoration only. */
  art?: ReactNode;
}) {
  // The border moved from the label to this wrapper so a footer can sit inside
  // the option's outline without sitting inside its click target.
  return (
    <div style={{
      border: `1px solid ${selected ? 'var(--accent)' : 'var(--border)'}`,
      borderRadius: 8,
      background: selected ? 'var(--bg-subtle)' : 'var(--surface)',
    }}>
      <label style={{
        display: 'flex', gap: 10, padding: 12,
        cursor: disabled ? 'default' : 'pointer',
      }}>
        {/* The card's own label names the radio; a second label inside it is invalid. */}
        <Radio
          name="project-recording-mode"
          checked={selected}
          disabled={disabled}
          onChange={onSelect}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 500 }}>{title}</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{desc}</div>
        </div>
        {art && <div style={{ flex: '0 0 112px', alignSelf: 'center' }}>{art}</div>}
      </label>
      {footer && <div style={{ padding: '0 12px 12px 44px' }}>{footer}</div>}
    </div>
  );
}
